import React from 'react'
import styled from 'styled-components'
import MapPageLayout from '../templates/map-page-layout'
import MapControl from '../controls/map-control'
import SidebarControl from '../controls/sidebar-control'
import IEventedComponent, { messageTypes } from '../components/IEventedComponent'
import * as parcelAgg from '../utils/parcelAggregrates'
import Dialog from '../controls/dialog'
import UploadFileDialog from '../controls/upload-file-dialog'
import ReportErrorDialog from '../controls/report-error-dialog'
import Callout from '../components/callout'
import NvraAreaWarning from '../content/nvra-area-search-warning.mdx'
import colours from '../lib/colours'
import { mapQueryResultsToLevyRates, queryFeatures } from '../utils/mapUtils'
import mapConfig from '../config/map-config'

const CalloutContainer = styled.div`
  margin: -20px -40px 0;
  padding: 10px 20px 10px 15px;
  font-weight: 300;
`

interface IProps {
  location: any
}
interface IState {
  parcelAggregates: any
  parcelData: any
  parcelsRequested: string[]
  parcelsSelected: string[]
  areaData: any
  areaJson: any
  rates: any
  isFetchingAreaData: boolean
  searchLocation: any
  showOutsideMsaWarning: boolean
  showInfoPopup: boolean
  DialogContent: any
  showUploadFileDialog: boolean
  showReportErrorDialog: boolean
}

class MapPage extends IEventedComponent<IProps, IState> {
  state = {
    parcelAggregates: parcelAgg.initialValues,
    parcelData: {}, // contains data for parcels that have been fetched, even if they are not currently selected
    parcelsRequested: [],
    parcelsSelected: [],
    areaData: {},
    areaJson: null,
    rates: null,
    isFetchingAreaData: false,
    searchLocation: null,
    showOutsideMsaWarning: false,
    showInfoPopup: false,
    DialogContent: NvraAreaWarning,
    showUploadFileDialog: false,
    showReportErrorDialog: false
  }

  componentDidMount() {
    const { location } = this.props
    if (location.state) {
      this.setState({ searchLocation: location.state })
    }
    this.getLevyRates()

    this.subscribe(messageTypes.onSearchLocationChanged, (searchLocation: any) => this.setState({ searchLocation }))
    this.subscribe(messageTypes.onParcelLevyLiabilitiesRequested, this.handleParcelLevyLiabilitiesRequested.bind(this))
    this.subscribe(messageTypes.onParcelLevyLiabilitiesReceived, this.handleParcelLevyLiabilitiesReceived.bind(this))
    this.subscribe(messageTypes.onParcelLevyLiabilitiesError, this.handleParcelLevyLiabilitiesError.bind(this))
    this.subscribe(messageTypes.onAreaLevyLiabilitiesRequested, this.handleAreaLevyLiabilitiesRequested.bind(this))
    this.subscribe(messageTypes.onAreaLevyLiabilitiesReceived, this.handleAreaLevyLiabilitiesReceived.bind(this))
    this.subscribe(messageTypes.onAreaLevyLiabilitiesError, this.handleAreaLevyLiabilitiesError.bind(this))
    this.subscribe(messageTypes.onParcelRemoved, this.handleParcelRemoved.bind(this))
    this.subscribe(messageTypes.onParcelReadded, this.handleParcelReadded.bind(this))
    this.subscribe(messageTypes.onOpenFileUploadDialog, () => this.setState({ showUploadFileDialog: true }))
    this.subscribe(messageTypes.onCloseFileUploadDialog, () => this.setState({ showUploadFileDialog: false }))
    this.subscribe(messageTypes.onOpenReportErrorDialog, () => this.setState({ showReportErrorDialog: true }))
    this.subscribe(messageTypes.onCloseReportErrorDialog, () => this.setState({ showReportErrorDialog: false }))
    this.subscribe(messageTypes.onUploadAreaAdded, this.handleUploadAreaAdded.bind(this))
    this.subscribe(messageTypes.onUploadAreaRemoved, this.handleUploadAreaRemoved.bind(this))
  }

  getLevyRates = async () => {
    const url = `${mapConfig.dynamicMapServices.emisMsa.url}/${mapConfig.dynamicMapServices.emisMsa.tables.msaLevyRates.layerId}`
    const results = await queryFeatures(url, null, '1=1', ['*'], false)
    const levyRates = mapQueryResultsToLevyRates(results)
    this.setState({
      rates: levyRates
    })
  }

  setInfoPopupState = (visible: boolean, DialogContent?: React.Component) => {
    this.setState({
      showInfoPopup: visible
    })

    if (DialogContent) {
      this.setState({ DialogContent })
    }
  }

  setShowOutsideMsaWarning = (visible: boolean) => {
    this.setState({
      showOutsideMsaWarning: visible
    })
  }

  handleParcelLevyLiabilitiesRequested = (parcelDetails: any) => {
    this.setState(prevState => {
      const parcelsRequested = [...prevState.parcelsRequested, parcelDetails.parcelSpi]
      return { ...prevState, parcelsRequested }
    })
  }

  handleParcelLevyLiabilitiesReceived = (parcelDetails: any) => {
    this.setState(prevState => {
      const spi = parcelDetails.parcelSpi
      const parcelsRequested = [...prevState.parcelsRequested].filter(x => x !== spi)
      const parcelsSelected = [...prevState.parcelsSelected]
      if (!parcelsSelected.includes(spi)) parcelsSelected.push(spi)
      const parcelData = {
        ...prevState.parcelData,
        ...{ [spi]: parcelDetails.parcelData }
      }
      const parcelAggregates = parcelAgg.computeAggregates(parcelData, parcelsSelected)
      return {
        ...prevState,
        parcelsRequested,
        parcelsSelected,
        parcelData,
        parcelAggregates
      }
    })
  }

  handleParcelLevyLiabilitiesError = (error: any) => {
    console.error(error)
    this.setState(prevState => {
      const parcelsRequested: string[] = []
      return { ...prevState, parcelsRequested }
    })
  }

  handleAreaLevyLiabilitiesRequested = () => {
    this.setState({
      isFetchingAreaData: true
    })
  }

  handleAreaLevyLiabilitiesReceived = (results: any) => {
    this.setState({
      isFetchingAreaData: false,
      areaData: results.results
    })
  }

  handleAreaLevyLiabilitiesError = (error: any) => {
    console.error(error)
    this.setState({
      isFetchingAreaData: false
    })
  }

  handleParcelRemoved = (parcelDetails: any) => {
    this.setState(prevState => {
      const spi = parcelDetails.parcelSpi
      const parcelsSelected = [...prevState.parcelsSelected].filter(x => x !== spi)
      const parcelData = { ...prevState.parcelData }
      const parcelAggregates = parcelAgg.computeAggregates(parcelData, parcelsSelected)
      return { ...prevState, parcelsSelected, parcelData, parcelAggregates }
    })
  }

  handleParcelReadded = (parcelDetails: any) => {
    this.setState(prevState => {
      const spi = parcelDetails.parcelSpi
      const parcelsSelected = [...prevState.parcelsSelected, spi]
      const parcelAggregates = parcelAgg.computeAggregates(prevState.parcelData, parcelsSelected)
      return { ...prevState, parcelsSelected, parcelAggregates }
    })
  }

  handleUploadAreaAdded = (results: any) => {
    this.setState({
      areaJson: results.areaJson
    })
  }

  handleUploadAreaRemoved = () => {
    this.setState({
      areaData: {},
      areaJson: null
    })
  }

  render() {
    const {
      searchLocation,
      parcelsSelected,
      parcelsRequested,
      parcelData,
      DialogContent,
      parcelAggregates,
      showInfoPopup,
      showOutsideMsaWarning,
      showUploadFileDialog,
      showReportErrorDialog,
      isFetchingAreaData,
      areaData,
      areaJson,
      rates
    } = this.state

    return (
      <MapPageLayout>
        <SidebarControl
          searchLocation={searchLocation}
          parcelsSelected={parcelsSelected}
          parcelData={parcelData}
          areaData={Object.keys(areaData).length > 1 ? { ...areaData, areaJson: areaJson } : null}
          rates={rates}
          parcelAggregates={parcelAggregates}
          fetching={!!parcelsRequested.length || isFetchingAreaData}
          showOutsideMsaWarning={showOutsideMsaWarning}
          setShowOutsideMsaWarning={this.setShowOutsideMsaWarning}
          setInfoPopupState={this.setInfoPopupState}
        />
        <MapControl
          parcelsSelected={parcelsSelected}
          parcelData={parcelData}
          searchLocation={searchLocation}
          showOutsideMsaWarning={showOutsideMsaWarning}
          setShowOutsideMsaWarning={this.setShowOutsideMsaWarning}
          setInfoPopupState={this.setInfoPopupState}
        />
        <Dialog
          modal={true}
          open={showInfoPopup}
          showHeader={true}
          showFooter={true}
          setPopupState={this.setInfoPopupState}
          heading="Warning" 
        >
          <CalloutContainer style={{ backgroundColor: colours.lightGreenBackground }}>
            <Callout type="warning">
              <DialogContent />
            </Callout>
          </CalloutContainer>
        </Dialog>
        <UploadFileDialog open={showUploadFileDialog} />
        <ReportErrorDialog open={showReportErrorDialog} />
      </MapPageLayout>
    )
  }
}

export default MapPage
